import axios, {AxiosResponse} from 'axios';
import {
    CodesRetrieveResponse, ColumnRetrieveResponse, DataExportRetrieveReceiversResponse, DataHistoriesRetrieveResponse,
    DataLibrariesRetrieveResponse, DataLibraryAuditTrailsResponse, DataLibraryRetrieveResponse, DataPageItemsResponse,
    DataSearchItemAuditTrailsResponse, ExcelExportResponse
} from '@/model/CommonModel';
import ApiUtils from '@/utils/ApiUtils';

export default class DataApi {

    /**
     * Sender Key는 헤더에 선택 된 (URI 상에 존재하는) Sender를 기준으로 한다.
     * @param sponsorKey 스폰서 키
     * @param searchItem 조회 조건
     * @param params 추가적인 params (senderKey가 절대 존재하면 안됨)
     */
    public static retrieveDataExportCasePreviews(sponsorKey: number | string, searchItem: any, params: any = {})
        : Promise<DataExportRetrieveReceiversResponse> {
        return axios
            .post(`/1.0/sponsors/${sponsorKey}/export/preview`, searchItem, {params})
            .then((res: AxiosResponse) => res.data);
    }
    
    /**
     * Sender Key는 헤더에 선택 된 (URI 상에 존재하는) Sender를 기준으로 한다.
     * @param sponsorKey 스폰서 키
     * @param searchItem 조회 조건
     * @param params 추가적인 params (senderKey가 절대 존재하면 안됨)
     */
    public static retrieveDataExportCaseAePreviews(sponsorKey: number | string, searchItem: any, params: any = {})
        : Promise<DataExportRetrieveReceiversResponse> {
        return axios
            .post(`/1.0/sponsors/${sponsorKey}/export/ae/preview`, searchItem, {params})
            .then((res: AxiosResponse) => res.data);
    }
    
    public static downloadDBSpecFile(sponsorKey: number | string): Promise<any> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/export/dictionary-download`, {responseType: 'blob'})
            .then((res: any) => ApiUtils.isFailToFileDownloadResponse(res)
                ? ApiUtils.handleFileDownloadFailResponse(res)
                : ApiUtils.handleFileDownloadSuccessResponse(res));
    }
    
    /**
     * Sender Key는 헤더에 선택 된 (URI 상에 존재하는) Sender를 기준으로 한다.
     * @param sponsorKey 스폰서 키
     * @param formData 조회 조건 및 선택된 Receiver, Sender 정보 (헤더에 있는 Sender가 아님)
     * @param params 추가적인 params (senderKey가 절대 존재하면 안됨)
     */
    public static exportCaseData(sponsorKey: number | string, formData: any, params: any = {}) {
        return axios
            .put(`/1.0/sponsors/${sponsorKey}/export`, formData, {params})
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveDataHistoriesColumns(): Promise<ColumnRetrieveResponse> {
        return axios
            .get(`/1.0/menus/MU-DT-HS/columns`)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveDataHistories(sponsorKey: number, params: any = {}): Promise<DataHistoriesRetrieveResponse> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/datas/history`, {params})
            .then((res: AxiosResponse) => res.data);
    }

    public static retrieveDataHistoryImportDetail(sponsorKey: number | string, dataKey: number | string,
                                                  params: any = {}): Promise<any> {
        return axios
            .get(`1.0/sponsors/${sponsorKey}/datas/history/${dataKey}/import-detail`, {params})
            .then((res) => res.data);
    }

    public static getDataHistoryImportDetailFileInfo(sponsorKey: number | string,
                                                     dataKey: number | string): Promise<any> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/datas/history/${dataKey}/file-info`)
            .then((res) => res.data.data);

    }
    
    public static downloadHistoryFile(sponsorKey: number, fileKey: number): Promise<any> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/files/${fileKey}`, {responseType: 'blob'})
            .then((res: any) => ApiUtils.isFailToFileDownloadResponse(res)
                ? ApiUtils.handleFileDownloadFailResponse(res)
                : ApiUtils.handleFileDownloadSuccessResponse(res));
    }
    
    public static retrieveDataLibraries(sponsorKey: number | string, params: any = {}): Promise<DataLibrariesRetrieveResponse> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/search-libraries`, {params})
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveDataCaseReportTypes(sponsorKey: number | string, type: string): Promise<CodesRetrieveResponse> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/search-libraries/items/${type}`)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static downloadDataImportTemplate(sponsorKey: number | string): Promise<any> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/import/template-download`, {responseType: 'blob'})
            .then((res: any) => ApiUtils.isFailToFileDownloadResponse(res)
                ? ApiUtils.handleFileDownloadFailResponse(res)
                : ApiUtils.handleFileDownloadSuccessResponse(res));
    }
    
    public static uploadCaseReportFile(sponsorKey: number | string, formData: FormData) {
        return axios
            .post(`/1.0/sponsors/${sponsorKey}/import/uploadFile`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }).then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveDataLibrary(sponsorKey: number | string, libraryKey: number | string, params: any = {showDeleted: true})
        : Promise<DataLibraryRetrieveResponse> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/search-libraries/${libraryKey}`, {params})
            .then((res: AxiosResponse) => res.data);
    }
    
    public static getLibraryItemAuditTrails(sponsorKey: number | string, libraryKey: number | string, params: any = {})
        : Promise<DataLibraryAuditTrailsResponse> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/search-libraries/${libraryKey}/audittrails`, {params})
            .then((res: AxiosResponse) => res.data);
    }
    
    public static getDataSearchItemRowAuditTrails(sponsorKey: number | string, libraryKey: number | string, seq: number)
        : Promise<DataSearchItemAuditTrailsResponse> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/search-libraries/${libraryKey}/items/${seq}/audittrails`)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveDataLibraryItems(sponsorKey: number | string): Promise<DataPageItemsResponse> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/search-libraries/items`)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static saveDataLibrary(sponsorKey: number | string, libraryKey: number | string, param: any) {
        return axios
            .put(`/1.0/sponsors/${sponsorKey}/search-libraries/${libraryKey}`, param)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static retrieveDataSetTreeItems(sponsorKey: number | string): Promise<any> {
        return axios
            .get(`/1.0/sponsors/${sponsorKey}/export/dataset`)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static downloadDataSet(sponsorKey: number | string, param: any) {
        return axios
            .put(`/1.0/sponsors/${sponsorKey}/export/dataset/download`, param)
            .then((res: AxiosResponse) => res.data);
    }
    
    public static exportDataHistoriesExcel(): Promise<ExcelExportResponse> {
        return axios
            .get(`/1.0/data-histories/export`)
            .then((res: AxiosResponse) => res.data);
    }
}
